import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { 
    Search, ShoppingBag, Gavel, Eye, Calendar, User, 
    ExternalLink, Loader2, CheckCircle,
    Pencil, Trash2, AlertTriangle
} from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { listingsAPI } from '@/lib/api';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import EditListingSheet from './EditListingSheet';

const AdminListingsTab = ({ initialFilter = {} }) => {
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [listingToDelete, setListingToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // Edit listing state
    const [editSheetOpen, setEditSheetOpen] = useState(false);
    const [listingToEdit, setListingToEdit] = useState(null);
    
    // Filters - initialize from props if provided
    const [typeFilter, setTypeFilter] = useState(initialFilter.type || 'all');
    const [statusFilter, setStatusFilter] = useState(initialFilter.status || 'active');
    
    // Infinite scroll
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const perPage = 20;
    const observerRef = useRef(null);
    const loadMoreRef = useRef(null);

    // Update filters when initialFilter changes
    useEffect(() => {
        if (initialFilter.type !== undefined || initialFilter.status !== undefined) {
            if (initialFilter.type === 'auction') {
                setTypeFilter('auction');
            } else if (initialFilter.type === 'direct_sale') {
                setTypeFilter('direct_sale');
            }
            if (initialFilter.status) {
                setStatusFilter(initialFilter.status);
            }
            setPage(1);
            setListings([]);
            setHasMore(true);
        }
    }, [initialFilter.type, initialFilter.status]);

    // Reset and fetch when filters change
    useEffect(() => {
        setListings([]);
        setPage(1);
        setHasMore(true);
        fetchListings(1, true);
    }, [typeFilter, statusFilter]);

    // Infinite scroll observer
    useEffect(() => {
        if (observerRef.current) observerRef.current.disconnect();
        
        observerRef.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
                    setPage(prev => prev + 1);
                }
            },
            { threshold: 0.1 }
        );
        
        if (loadMoreRef.current) {
            observerRef.current.observe(loadMoreRef.current);
        }
        
        return () => {
            if (observerRef.current) observerRef.current.disconnect();
        };
    }, [hasMore, loading, loadingMore]);

    // Load more when page changes
    useEffect(() => {
        if (page > 1) {
            fetchListings(page, false);
        }
    }, [page]);

    const fetchListings = async (pageNum = 1, reset = false) => {
        if (reset) {
            setLoading(true);
        } else {
            setLoadingMore(true);
        }
        
        try {
            const params = {
                limit: perPage,
                skip: (pageNum - 1) * perPage,
                sort_by: 'created_at',
                sort_order: 'desc'
            };
            
            // Type filter
            if (typeFilter !== 'all') {
                params.listing_type = typeFilter;
            }
            
            // Status filter
            if (statusFilter !== 'all') {
                params.status = statusFilter;
            }
            
            const res = await listingsAPI.getAll(params);
            const newListings = res.data?.listings || res.data || [];
            const total = res.data?.total || 0;
            
            setTotalCount(total);
            
            if (reset) {
                setListings(newListings);
            } else {
                setListings(prev => [...prev, ...newListings]);
            }
            
            setHasMore(newListings.length === perPage && (pageNum * perPage) < total);
        } catch (error) {
            console.error('Error fetching listings:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Filter by search query
    const filteredListings = listings.filter(listing => 
        !searchQuery || 
        listing.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        listing.seller_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        listing.seller_username?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleDelete = async () => {
        if (!listingToDelete) return;
        
        setDeleting(true);
        try {
            await listingsAPI.delete(listingToDelete.listing_id);
            toast.success('Anunțul a fost șters cu succes');
            setDeleteDialogOpen(false);
            setListingToDelete(null);
            // Reset and refetch
            setListings([]);
            setPage(1);
            setHasMore(true);
            fetchListings(1, true);
        } catch (error) {
            console.error('Error deleting listing:', error);
            toast.error(error.response?.data?.detail || 'Eroare la ștergerea anunțului');
        } finally {
            setDeleting(false);
        }
    };

    const handleEdit = (listing) => {
        setListingToEdit(listing);
        setEditSheetOpen(true);
    };

    const handleEditSuccess = () => {
        // Reset and refetch after edit
        setListings([]);
        setPage(1);
        setHasMore(true);
        fetchListings(1, true);
    };

    const getStatusBadge = (listing) => {
        if (listing.status === 'sold') {
            return <Badge className="bg-green-500 text-white">✅ Vândut</Badge>;
        }
        if (listing.status === 'active') {
            return <Badge className="bg-blue-500 text-white">🟢 Activ</Badge>;
        }
        if (listing.status === 'cancelled') {
            return <Badge className="bg-red-500 text-white">❌ Anulat</Badge>;
        }
        return <Badge variant="secondary">{listing.status}</Badge>;
    };

    const getTypeBadge = (listing) => {
        if (listing.listing_type === 'auction') {
            return <Badge variant="outline" className="border-purple-500 text-purple-600">Licitație</Badge>;
        }
        return <Badge variant="outline" className="border-green-500 text-green-600">Preț fix</Badge>;
    };

    return (
        <div className="space-y-6" data-testid="admin-listings-tab">
            {/* Header with Filters */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-bold">Gestionare Anunțuri</h2>
                        <p className="text-sm text-muted-foreground">
                            Vizualizează și administrează toate anunțurile
                        </p>
                    </div>
                    
                    {/* Filter Dropdowns */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <Select value={typeFilter} onValueChange={(val) => {
                            setTypeFilter(val);
                        }}>
                            <SelectTrigger className="w-full sm:w-[150px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    📋 Toate
                                </SelectItem>
                                <SelectItem value="direct_sale">
                                    <div className="flex items-center gap-2">
                                        <ShoppingBag className="w-4 h-4 text-green-600" />
                                        Vânzări
                                    </div>
                                </SelectItem>
                                <SelectItem value="auction">
                                    <div className="flex items-center gap-2">
                                        <Gavel className="w-4 h-4 text-purple-600" />
                                        Licitații
                                    </div>
                                </SelectItem>
                            </SelectContent>
                        </Select>
                        
                        <Select value={statusFilter} onValueChange={(val) => {
                            setStatusFilter(val);
                        }}>
                            <SelectTrigger className="w-full sm:w-[160px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    📋 Toate
                                </SelectItem>
                                <SelectItem value="active">
                                    🟢 Deschise
                                </SelectItem>
                                <SelectItem value="sold">
                                    ✅ Finalizate
                                </SelectItem>
                                <SelectItem value="cancelled">
                                    ❌ Anulate
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                        placeholder="Caută după titlu sau vânzător..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                    />
                </div>

                {/* Stats Summary */}
                <div className="text-sm text-muted-foreground">
                    <span>
                        {typeFilter === 'all' ? 'Listări' : (typeFilter === 'auction' ? 'Licitații' : 'Vânzări')}
                        {statusFilter === 'all' ? '' : (statusFilter === 'active' ? ' deschise' : statusFilter === 'sold' ? ' finalizate' : ' anulate')}:
                        <strong className="ml-1 text-foreground">{totalCount}</strong>
                    </span>
                </div>
            </div>

            {/* Listings Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6 space-y-4">
                            {[...Array(5)].map((_, i) => (
                                <div key={i} className="flex items-center gap-4">
                                    <Skeleton className="w-16 h-16 rounded-lg" />
                                    <div className="flex-1 space-y-2">
                                        <Skeleton className="h-4 w-3/4" />
                                        <Skeleton className="h-3 w-1/2" />
                                    </div>
                                    <Skeleton className="h-6 w-20" />
                                </div>
                            ))}
                        </div>
                    ) : filteredListings.length === 0 ? (
                        <div className="p-12 text-center text-muted-foreground">
                            <ShoppingBag className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Nu există {typeFilter === 'auction' ? 'licitații' : typeFilter === 'direct_sale' ? 'vânzări' : 'listări'} {statusFilter === 'active' ? 'deschise' : statusFilter === 'sold' ? 'finalizate' : statusFilter === 'cancelled' ? 'anulate' : ''}</p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {filteredListings.map((listing) => (
                                <div key={listing.listing_id} className="p-3 md:p-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-start gap-3 md:gap-4">
                                        {/* Image - use thumbnail */}
                                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                                            {listing.images?.[0] ? (
                                                <img 
                                                    src={listing.images[0].replace('/api/images/', '/api/images/thumb_')} 
                                                    alt={listing.title}
                                                    className="w-full h-full object-cover"
                                                    loading="lazy"
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <ShoppingBag className="w-6 h-6 text-gray-400" />
                                                </div>
                                            )}
                                        </div>

                                        {/* Details */}
                                        <div className="flex-1 min-w-0">
                                            {/* Title + Actions on mobile */}
                                            <div className="flex items-start justify-between gap-2">
                                                <h4 className="font-medium text-sm md:text-base line-clamp-2 md:truncate">{listing.title}</h4>
                                                {/* Actions - hidden on mobile, shown on md+ */}
                                                <div className="hidden md:flex items-center gap-2 shrink-0">
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => handleEdit(listing)}
                                                        title="Editează"
                                                        data-testid={`edit-listing-${listing.listing_id}`}
                                                    >
                                                        <Pencil className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => {
                                                            setListingToDelete(listing);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                        title="Șterge"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                    <Link to={`/listing/${listing.slug || listing.listing_id}`} target="_blank">
                                                        <Button variant="outline" size="sm" title="Vezi anunțul">
                                                            <ExternalLink className="w-4 h-4" />
                                                        </Button>
                                                    </Link>
                                                </div>
                                            </div>
                                            
                                            {/* Seller */}
                                            <div className="flex items-center gap-2 mt-1 text-xs md:text-sm text-muted-foreground">
                                                <User className="w-3 h-3" />
                                                <span className="truncate">{listing.seller_name || listing.seller_username}</span>
                                            </div>
                                            
                                            {/* Badges */}
                                            <div className="flex items-center gap-1.5 md:gap-2 mt-2 flex-wrap">
                                                {getTypeBadge(listing)}
                                                {getStatusBadge(listing)}
                                            </div>
                                            
                                            {/* Price and stats */}
                                            <div className="flex items-center gap-3 md:gap-4 mt-2 text-xs md:text-sm flex-wrap">
                                                <span className="font-semibold text-primary">
                                                    {formatCurrency(listing.current_price || listing.starting_price || 0)}
                                                </span>
                                                <span className="flex items-center gap-1 text-muted-foreground">
                                                    <Eye className="w-3 h-3" />
                                                    {listing.views || 0}
                                                </span>
                                                <span className="hidden sm:flex items-center gap-1 text-muted-foreground">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(listing.created_at)}
                                                </span>
                                                {listing.listing_type === 'auction' && listing.bid_count > 0 && (
                                                    <span className="text-purple-600">
                                                        {listing.bid_count} {listing.bid_count === 1 ? 'ofertă' : 'oferte'}
                                                    </span>
                                                )}
                                            </div>
                                            
                                            {/* Mobile actions - shown only on mobile */}
                                            <div className="flex items-center gap-2 mt-3 md:hidden">
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    className="flex-1"
                                                    onClick={() => handleEdit(listing)}
                                                >
                                                    <Pencil className="w-4 h-4 mr-1" />
                                                    Editează
                                                </Button>
                                                <Link to={`/listing/${listing.slug || listing.listing_id}`} target="_blank" className="flex-1">
                                                    <Button variant="outline" size="sm" className="w-full">
                                                        <ExternalLink className="w-4 h-4 mr-1" />
                                                        Vezi
                                                    </Button>
                                                </Link>
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    onClick={() => {
                                                        setListingToDelete(listing);
                                                        setDeleteDialogOpen(true);
                                                    }}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Infinite Scroll Loader */}
            {hasMore && (
                <div ref={loadMoreRef} className="flex justify-center py-4">
                    {loadingMore && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Se încarcă...</span>
                        </div>
                    )}
                </div>
            )}
            
            {!hasMore && listings.length > 0 && (
                <p className="text-center text-sm text-muted-foreground py-4">
                    Ai ajuns la final ({totalCount} rezultate)
                </p>
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Confirmare ștergere
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Ești sigur că vrei să ștergi anunțul <strong>&quot;{listingToDelete?.title}&quot;</strong>?
                            <br /><br />
                            Această acțiune este ireversibilă și va șterge permanent anunțul din platformă.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Anulează</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Se șterge...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Șterge anunțul
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Edit Listing Sheet */}
            <EditListingSheet
                listing={listingToEdit}
                open={editSheetOpen}
                onOpenChange={setEditSheetOpen}
                onSuccess={handleEditSuccess}
            />
        </div>
    );
};

export default AdminListingsTab;
